import { Note } from "@fidget/common/components/surfaces/note/note";
import { intl } from "@fidget/common/i18n";
import {
  RoomSummaryAttachmentsMessage,
  RoomSummaryContext,
  RoomSummaryEvent,
  RoomSummaryEventTag
} from "@fidget/common/models/chat/roomSummaryContext";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import classNames from "classnames";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import React from "react";
import styles from "./roomSummary.css";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";

interface Props {
  context: RoomSummaryContext;
  renderUser(userId: string): React.ReactNode;
}
export const RoomSummary = observer(({ context: { events, roomId }, renderUser }: Props) => {
  return (
    <Note className={styles["border-container"]}>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles["content-font"]}>{intl.formatMessage("roomSummary.title")}</p>
          <p className={classNames(styles["description-font"], styles.item)}>
            {intl.formatMessage("roomSummary.roomId")}: {roomId}
          </p>
        </div>
        {events.map((e, i) => (
          <div
            className={styles.item}
            key={i /* key = index because room summary events are completely static */}
          >
            <RoomSummaryItem renderUser={renderUser} event={e} />
          </div>
        ))}
      </div>
    </Note>
  );
});

interface RoomSummaryItemProps {
  event: RoomSummaryEvent;
  renderUser(userId: string): React.ReactNode;
}
const RoomSummaryItem = observer(({ event, renderUser }: RoomSummaryItemProps) => {
  const renderHeading = () => {
    return (
      <p className={styles["description-font"]}>
        {format(event.timestamp, "HH:mm")} | {renderUser(event.authorId)}
      </p>
    );
  };

  const renderAsTextItem = (content: string | undefined) => {
    return <p className={styles["content-font"]}>{content}</p>;
  };

  const renderAttachmentsItem = ({ context, message }: RoomSummaryAttachmentsMessage) => {
    return (
      <div className={styles.container}>
        {renderAsTextItem(context.message || message)}
        <div className={styles["link-container"]}>
          <i className={classNames("icon-attach-dashboard", styles["content-font"])} />
          {context.attachments.map((attachment) => (
            <a
              key={attachment.id}
              target={"_blank"}
              href={getWheelhouseDownloadUrl(attachment.token)}
              className={styles["content-font"]}
            >
              {attachment.fileName}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (event.tag) {
      case RoomSummaryEventTag.SummaryTextMessage:
        return renderAsTextItem(event.message);
      case RoomSummaryEventTag.SummaryLektaMessage:
        return renderAsTextItem(event.context.message || event.message);
      case RoomSummaryEventTag.SummaryAttachmentsMessage:
        return renderAttachmentsItem(event);
      default:
        assertUnreachable(event);
        return "";
    }
  };

  return (
    <div className={styles.container}>
      {renderHeading()}
      {renderContent()}
    </div>
  );
});
